import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Image as ImageIcon, Film, Mic, FileText, Download } from 'lucide-react';

/**
 * Load authenticated media and render image / video / audio / document.
 * path = relative media_filename from API (e.g. tenant/inbound/xxx.jpg)
 * Supports play (audio/video) + download for all types (incl. group messages).
 */
export default function ChatMedia({
  path,
  mediaType,
  mimetype,
  caption,
  fileName,
  className = '',
}) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [blob, setBlob] = useState(null);

  useEffect(() => {
    if (!path) return undefined;
    let objectUrl = null;
    let cancelled = false;
    setErr(null);
    setUrl(null);
    setBlob(null);
    api
      .get(`/whatsapp/media/file?path=${encodeURIComponent(path)}`, { responseType: 'blob' })
      .then((r) => {
        if (cancelled) return;
        const data = r.data;
        // Prefer MIME from server blob or message mimetype (voice notes need audio/ogg)
        let typed = data;
        const serverType = data?.type;
        if ((!serverType || serverType === 'application/octet-stream') && mimetype) {
          typed = new Blob([data], { type: mimetype.split(';')[0].trim() || mimetype });
        }
        objectUrl = URL.createObjectURL(typed);
        setBlob(typed);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setErr('No se pudo cargar el archivo');
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path, mimetype]);

  const downloadName =
    fileName ||
    (path ? path.split('/').pop() : null) ||
    (mediaType === 'audio' ? 'audio.ogg' : mediaType === 'document' ? 'archivo' : 'media');

  const doDownload = () => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadName;
    a.rel = 'noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (!path) {
    return (
      <div className="text-xs opacity-80 flex items-center gap-1">
        <FileText className="h-3.5 w-3.5" />
        Media no disponible
      </div>
    );
  }

  if (err) {
    return <div className="text-xs text-[hsl(var(--danger))]">{err}</div>;
  }

  if (!url) {
    return <div className="text-xs opacity-70 animate-pulse">Cargando media…</div>;
  }

  const type = (mediaType || '').toLowerCase();
  const mime = (mimetype || blob?.type || '').toLowerCase();
  const isImage = type === 'image' || mime.startsWith('image/');
  const isVideo = type === 'video' || mime.startsWith('video/');
  const isAudio = type === 'audio' || mime.startsWith('audio/');

  return (
    <div className={`space-y-1.5 ${className}`}>
      {isImage && (
        <a href={url} target="_blank" rel="noreferrer" className="block">
          <img
            src={url}
            alt={caption || 'imagen'}
            className="max-w-full max-h-64 rounded-[10px] object-contain bg-black/10"
          />
        </a>
      )}
      {isVideo && (
        <video src={url} controls className="max-w-full max-h-72 rounded-[10px] bg-black" playsInline />
      )}
      {isAudio && (
        <div className="flex flex-col gap-1.5 min-w-[220px] max-w-sm">
          <div className="flex items-center gap-2 rounded-xl border border-border/60 bg-background/40 px-2.5 py-2">
            <Mic className="h-4 w-4 shrink-0 opacity-70" />
            <audio src={url} controls preload="metadata" className="w-full h-9 flex-1" />
          </div>
          <button
            type="button"
            onClick={doDownload}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline self-start"
          >
            <Download className="h-3.5 w-3.5" />
            Descargar audio
          </button>
        </div>
      )}
      {!isImage && !isVideo && !isAudio && (
        <div className="flex flex-col gap-1.5">
          <div className="inline-flex items-center gap-2 text-sm rounded-lg border border-border px-3 py-2 bg-background/40 max-w-xs">
            <FileText className="h-4 w-4 shrink-0 opacity-70" />
            <span className="truncate text-xs">{downloadName}</span>
          </div>
          <button
            type="button"
            onClick={doDownload}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline self-start"
          >
            <Download className="h-3.5 w-3.5" />
            Descargar archivo
          </button>
        </div>
      )}
      {(isImage || isVideo) && (
        <button
          type="button"
          onClick={doDownload}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary hover:underline"
        >
          <Download className="h-3 w-3" />
          Descargar
        </button>
      )}
      {caption
        && caption !== '[imagen]'
        && caption !== '[video]'
        && caption !== '[audio]'
        && caption !== '[documento]'
        && caption !== '[nota de voz]'
        && (
          <div className="whitespace-pre-wrap text-sm mt-0.5">{caption}</div>
        )}
    </div>
  );
}

export function MediaTypeIcon({ type, className = 'h-3.5 w-3.5' }) {
  const t = (type || '').toLowerCase();
  if (t === 'image') return <ImageIcon className={className} />;
  if (t === 'video') return <Film className={className} />;
  if (t === 'audio') return <Mic className={className} />;
  return <FileText className={className} />;
}
